import {useTheme} from "@mui/material/styles";
import {useEffect, useReducer} from "react";

/**
 * Half the leading the browser adds around an inline run — the padding a bar needs to grow from its
 * own content box to the full line box the native selection highlight is painted over.
 *
 * MEASURED, never assumed. CSS deliberately leaves the content box's height to the browser (CSS 2.1
 * §10.6.1 — "based on the font"), and every engine takes it from the font's own ascent + descent,
 * so no constant is right for more than one face: League Spartan lands near 0.94em, where a guessed
 * 1.2em left every bar about 4px shorter than the selection it exists to match. A measurement has
 * no such failure mode — change the theme font and the bars re-tune themselves.
 *
 * The measurement is cached per (family, size, leading) and taken once more after the web fonts
 * land, because the first bar can render while the fallback face is still the one on screen.
 */

/** Used where there is no layout engine to measure with (jsdom). Never reached in a browser. */
const FALLBACK_CONTENT_BOX_EM = 0.94;

const cache = new Map<string, number>();

const listeners = new Set<() => void>();

let watchingFonts = false;

/**
 * The probe is an INLINE span inside a positioned host, and the distinction is the whole point:
 * `position: absolute` would compute `display: block`, and a block box is exactly one line box tall
 * — the number we are trying to tell apart from the content box.
 */
const measureContentBox = (fontFamily: string, fontSize: number, lineHeight: number): number => {
    if (typeof document === 'undefined') return fontSize * FALLBACK_CONTENT_BOX_EM;

    const host = document.createElement('div');
    host.style.cssText = 'position:absolute;top:0;left:-9999px;visibility:hidden;pointer-events:none';
    host.style.fontFamily = fontFamily;
    host.style.fontSize = `${fontSize}px`;
    host.style.lineHeight = String(lineHeight);

    const probe = document.createElement('span');
    probe.style.cssText = 'margin:0;padding:0;border:0;white-space:pre';
    probe.textContent = 'Mg';

    host.appendChild(probe);
    document.body.appendChild(host);
    const contentBox = probe.getBoundingClientRect().height;
    host.remove();

    return contentBox || fontSize * FALLBACK_CONTENT_BOX_EM;
};

const paddingFor = (fontFamily: string, fontSize: number, lineHeight: number): number => {
    const key = `${fontFamily}|${fontSize}|${lineHeight}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    // Clamped at zero: a font whose content box already fills the line box wants no padding, and a
    // negative one would shrink the bar below the characters it covers.
    const padding = Math.max(0, (fontSize * lineHeight - measureContentBox(fontFamily, fontSize, lineHeight)) / 2);
    cache.set(key, padding);

    return padding;
};

const watchFonts = () => {
    if (watchingFonts || typeof document === 'undefined' || !document.fonts) return;
    watchingFonts = true;

    void document.fonts.ready.then(() => {
        cache.clear();
        listeners.forEach(listener => listener());
    });
};

/** The bar's vertical padding as a CSS length, re-read once the real face has loaded. */
export const useSurveyBlackoutBarPadding = (fontSize: number, lineHeight: number): string => {
    const {typography} = useTheme();
    const fontFamily = typography.fontFamily ?? 'inherit';
    const [, remeasure] = useReducer((tick: number) => tick + 1, 0);

    useEffect(() => {
        watchFonts();
        listeners.add(remeasure);
        return () => void listeners.delete(remeasure);
    }, []);

    return `${paddingFor(fontFamily, fontSize, lineHeight)}px`;
};
